import { Row } from '@advanza/grid'
import { WhatsappSvg } from '@advanza/misc/WhatsappSvg'
import { Ellipsis, HoverText, Icon } from '@advanza/ui'
import ManualConfirmButton from 'components/matches/ManualConfirmButton'
import ManualReclaimButton from 'components/matches/ManualReclaimButton'
import PartialMatchButton from 'components/matches/PartialMatchButton'
import TableList from 'components/TableList'
import Balloon from 'components/ui/Balloon'
import FormattedTime from 'components/ui/FormattedTime'
import { formatDistanceStrictWrap, utcToLocal } from 'date'
import { useAppSelector } from 'hooks/hooks'
import { useList } from 'hooks/listHooks'
import { CHATBOT_LEAD_COLOR, getChatbotLeadLabel, isChatbotLead } from 'misc/chatbotLead'
import { FormattedNumber } from 'react-intl'
import { Link } from 'react-router-dom'
import matchesSchema from 'schemes/matchesSchema'
import style from './matchList.module.css'

interface Props {
    selectable?: boolean
    filterId: string
    defaultOptions?: any
    showCols?: string[]
    hideExport?: boolean
}

const MatchList = ({
    selectable,
    filterId,
    defaultOptions = {},
    showCols,
    hideExport = true,
}: Props) => {
    const services = useAppSelector((state) => state.services.entities.services)
    const { entities, ...rest } = useList(
        filterId,
        {
            url: 'office/matches/get-list',
            schema: matchesSchema,
            reducerKey: 'matches',
        },
        defaultOptions,
    )

    function getUrl(id: number) {
        return `/service/matches/match/${id}`
    }

    function renderColumns(id: number) {
        const { matches } = entities
        const match = matches[id]
        const service = (services && services[match.serviceId]) || {}

        const invalidRequestBadge =
            match.requestIsValid === false ? (
                <Balloon content="Invalid request">
                    <Icon style={{ color: '#ff4545' }}>gpp_bad</Icon>
                </Balloon>
            ) : null
        const confirmBlockedBadge =
            match.requestIsValid !== false && match.confirmBlockedReason ? (
                <Balloon content={match.confirmBlockedReason}>
                    <Icon style={{ color: '#f46511' }}>error</Icon>
                </Balloon>
            ) : null
        const confirmModalBadges =
            invalidRequestBadge || confirmBlockedBadge ? (
                <ManualConfirmButton
                    match={match}
                    renderTrigger={({ open }: { open: () => void }) => (
                        <button
                            type="button"
                            onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                open()
                            }}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                cursor: 'pointer',
                            }}>
                            {invalidRequestBadge}
                            {confirmBlockedBadge}
                        </button>
                    )}
                />
            ) : null
        const confirmCell = confirmModalBadges || <ManualConfirmButton match={match} />

        const firstChoiceBadge = match.first_choice ? (
            <Balloon content="First choice">
                <Icon>verified_user</Icon>
            </Balloon>
        ) : null
        const exclusiveBadge =
            match.confirmed &&
            (match.isSigned || match.isFreeTrialLead) &&
            match.exclusive_label &&
            !match.exclusive_expired ? (
                <Balloon content="Exclusive">
                    <Icon>diamond_shine</Icon>
                </Balloon>
            ) : null
        // Sits with the other lead-nature badges rather than in the provenance column, because
        // the provider-scoped lists leave that column out of `showCols` entirely.
        const chatbotBadge = isChatbotLead(match.requestType) ? (
            <Balloon content={getChatbotLeadLabel(match.requestSource)}>
                <Icon name="smart_toy" style={{ color: CHATBOT_LEAD_COLOR }} />
            </Balloon>
        ) : null
        const leadBadgesCell =
            firstChoiceBadge || exclusiveBadge || chatbotBadge ? (
                <div className="flex items-center gap-1 flex-wrap">
                    {firstChoiceBadge}
                    {exclusiveBadge}
                    {chatbotBadge}
                </div>
            ) : (
                ' '
            )

        const postConfirmBase = match.confirmed ?? match.created
        const postConfirmRef = match.confirmed ? 'confirmation' : 'match creation'
        const formatRelative = (date: typeof match.created, base: typeof match.created) =>
            formatDistanceStrictWrap(date, base, { addSuffix: false })
        const EventsMap = {
            confirmed: {
                color: 'blue',
                content: `confirmed ${formatRelative(match.confirmed, match.created)} after match creation`,
            },
            is_clicked: {
                color: 'greenBright',
                content: `pro match mail click ${formatRelative(match.is_clicked, postConfirmBase)} after ${postConfirmRef}`,
            },
            is_visited: {
                color: 'purple',
                content: `user visit ${formatRelative(match.is_visited, postConfirmBase)} after ${postConfirmRef}`,
            },
            is_read: {
                color: 'green',
                content: `pro visit ${formatRelative(match.is_read, postConfirmBase)} after ${postConfirmRef}`,
            },
            phone_view: {
                color: 'yellow',
                content: `phone view ${formatRelative(match.phone_view, postConfirmBase)} after ${postConfirmRef}`,
            },
            pro_clicked_whatsapp: {
                color: 'greenWhatsapp',
                content: `pro clicked whatsapp ${formatRelative(match.pro_clicked_whatsapp, postConfirmBase)} after ${postConfirmRef}`,
                icon: <WhatsappSvg width={10} height={10} style={{ color: 'white' }} />,
            },
            pro_email_clicked: {
                color: 'darkblue',
                content: `pro email clicked ${formatRelative(match.pro_email_clicked, postConfirmBase)} after ${postConfirmRef}`,
                icon: <Icon white name={'mail'} fontSize={10} />,
            },
            hired: {
                color: 'orange',
                content: `hired ${formatRelative(match.hired, postConfirmBase)} after ${postConfirmRef}`,
            },
            finished: {
                color: 'yellow',
                content: `marked finished ${formatRelative(match.hired, postConfirmBase)} after ${postConfirmRef}`,
            },
            reclaimed: {
                color: 'magenta',
                content: `reclaimed ${formatRelative(match.reclaimed, postConfirmBase)} after ${postConfirmRef}`,
            },
            rejected: {
                color: 'black',
                content: `rejected ${formatRelative(match.rejected, postConfirmBase)} after ${postConfirmRef}`,
            },
            from_dashboard: {
                color: 'black',
                content: 'Extra match added on ' + utcToLocal(match.created),
            },
            noInterestDate: {
                color: 'red',
                content: `declined ${formatRelative(match.noInterestDate, postConfirmBase)} after ${postConfirmRef} (${match.noInterestReason})`,
            },
        }
        const fields = Object.keys(match)
            //@ts-ignore
            .map((key) => EventsMap[key] && match[key] && { ...EventsMap[key], date: match[key] })
            .filter(Boolean)

        fields.sort((a, b) => {
            a = new Date(a.date)
            b = new Date(b.date)
            return a > b ? 1 : a < b ? -1 : 0
        })

        const checkpointStripes = (
            <Row style={{ flexWrap: 'noWrap', margin: 0 }}>
                {fields.map((checkpoint) => {
                    return (
                        <Balloon key={checkpoint.content} content={checkpoint.content}>
                            <div
                                style={{
                                    width: '1em',
                                    height: '1em',
                                    borderRadius: '50%',
                                    marginRight: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                                className={style[checkpoint.color]}>
                                {checkpoint.icon}
                            </div>
                        </Balloon>
                    )
                })}
            </Row>
        )

        return [
            <small className="text">
                <Link to={getUrl(id)}>#{id}</Link>
            </small>,
            <div>
                <Link
                    title="to pro"
                    className="plain"
                    to={`/service/providers/provider/${match.service_provider_id}`}>
                    {match.rejected ? (
                        <span className="line-through">{match.business_name}</span>
                    ) : (
                        match.business_name
                    )}
                    {match.isSigned && (
                        <img
                            style={{
                                verticalAlign: 'middle',
                                paddingLeft: 3,
                                display: 'inline-block',
                            }}
                            src="https://static.trustoo.nl/icon/premium.svg"
                            width="16"
                        />
                    )}
                    {match.isFreeTrialLead && (
                        <Balloon content="Free trial match">
                            <Icon
                                name="explosion"
                                green
                                fontSize={16}
                                style={{
                                    verticalAlign: 'text-bottom',
                                    paddingLeft: 3,
                                }}
                            />
                        </Balloon>
                    )}
                </Link>
            </div>,
            leadBadgesCell,
            match.fromCandidateMatch ? (
                <Balloon content="From candidate match">
                    <Icon name="switch_access_shortcut" />
                </Balloon>
            ) : match.from_dashboard ? (
                <Balloon content="Extra match from dashboard">
                    <Icon>person_add</Icon>
                </Balloon>
            ) : (
                ''
            ),
            <div>
                {(match.chargeAmount !== null || match.chargeAmountAbs !== null) && (
                    <b>
                        <small
                            className={
                                match.chargePaymentStatus === 'paid'
                                    ? style.chargePaid
                                    : match.chargePaymentStatus === 'invoiced_unpaid'
                                      ? style.chargeInvoicedUnpaid
                                      : style.chargeNotInvoiced
                            }
                            title={
                                match.chargePaymentStatus === 'paid'
                                    ? 'Invoice paid or fulfilled'
                                    : match.chargePaymentStatus === 'invoiced_unpaid'
                                      ? 'Invoiced, payment not yet received'
                                      : 'Not yet invoiced'
                            }>
                            {Number(match.chargeAmount) === 0 ? (
                                <span className="line-through">
                                    <FormattedNumber
                                        style="currency"
                                        currency="eur"
                                        value={Number(match.chargeAmountAbs)}
                                    />
                                </span>
                            ) : (
                                <FormattedNumber
                                    style="currency"
                                    currency="eur"
                                    value={Number(match.chargeAmount)}
                                />
                            )}
                            {match.chargeDescriptions?.includes('booster surcharge') && (
                                <Icon fontSize={19} name="insights" title="Lead booster" />
                            )}
                        </small>
                    </b>
                )}
            </div>,
            confirmCell,
            <div className="max-w-32 overflow-hidden text-ellipsis">
                <Link
                    title={`to request by ${match.userName || match.userEmail}`}
                    className="plain"
                    to={`/service/requests/request/${match.request_id}`}>
                    {match.userName || match.userEmail}
                </Link>
            </div>,
            checkpointStripes,
            <Ellipsis className="!max-w-[86px]" fullTextOnHover>
                {service.name}
            </Ellipsis>,
            <span className="score fsize lg">{match.nrMessages}</span>,
            match.is_partial_match && (
                <HoverText trigger={<PartialMatchButton matchId={match.match_id} />}>
                    Is partial match
                </HoverText>
            ),
            <small className="c-grey>">
                <FormattedTime date={match.created} />
            </small>,
            <ManualReclaimButton lead={match} id={match.match_id} leadType="matches" />,
            match.leadScore !== null && match.leadScore !== undefined && (
                <Balloon content="Current lead score">
                    <span className="score fsize lg">{match.leadScore}</span>
                </Balloon>
            ),
        ]
    }

    function renderRow(id: number) {
        return {
            url: getUrl(id),
            cols: renderColumns(id),
            id,
        }
    }

    const definitions = {
        id: { name: '#', preventRedirect: true },
        name: { name: 'pro', preventRedirect: true },
        firstChoice: { name: '' },
        from_dashboard: { name: '' },
        charge: { name: 'charge' },
        confirm: { name: 'inv', preventRedirect: true },
        userName: { name: 'user' },
        stripes: { name: ' ' },
        service: { name: 'service' },
        nrMsgs: { name: 'msg' },
        partialMatch: { name: 'p', preventRedirect: true },
        created: { name: 'Time' },
        reclaim: { name: 'rec', preventRedirect: true },
        leadScore: { name: 'LS' },
    }
    return (
        <TableList
            {...rest}
            selectable={selectable}
            renderRow={renderRow}
            definitions={definitions}
            showCols={showCols}
            addListFeatures
            hideExport={hideExport}
            hideOrder
        />
    )
}

export default MatchList
